import { createFileRoute, useRouter, Link } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import { computeSummen, formatEuroDe, parseMoneyOr, ZERO_MONEY } from '../lib/money-display';
import { UST_SATZ_DEFAULT } from '../lib/steuer-konstanten';

interface Kunde {
  id: string;
  kundennummer: string;
  firmenname: string;
}

interface Objekt {
  id: string;
  kundeId: string;
  bezeichnung: string;
  objektnummer: string | null;
}

interface PositionForm {
  bezeichnung: string;
  beschreibung: string;
  menge: string;
  einheit: string;
  einzelpreisNetto: string;
  ustSatz: string;
  rabattProzent: string;
}

function emptyPosition(): PositionForm {
  return {
    bezeichnung: '',
    beschreibung: '',
    menge: '1',
    einheit: 'Std',
    einzelpreisNetto: '',
    ustSatz: UST_SATZ_DEFAULT,
    rabattProzent: '0',
  };
}

export const Route = createFileRoute('/angebote/neu')({
  beforeLoad: () => requireAccess('/api/v1/angebote'),
  component: AngebotNeuPage,
});

function AngebotNeuPage() {
  const router = useRouter();
  const kundenQ = useQuery({
    queryKey: ['kunden', 'lookup'],
    queryFn: () => api<{ items: Kunde[] }>('/api/v1/kunden'),
  });
  const objekteQ = useQuery({
    queryKey: ['objekte', 'lookup'],
    queryFn: () => api<{ items: Objekt[] }>('/api/v1/objekte'),
  });

  const [kundeId, setKundeId] = useState('');
  const [objektId, setObjektId] = useState('');
  const [titel, setTitel] = useState('');
  const [gueltigBis, setGueltigBis] = useState(defaultGueltigBis());
  const [zahlungszielTage, setZahlungszielTage] = useState('14');
  const [notizen, setNotizen] = useState('');
  const [positionen, setPositionen] = useState<PositionForm[]>([emptyPosition()]);
  const [error, setError] = useState<unknown>(null);

  const objekteDesKunden = (objekteQ.data?.items ?? []).filter((o) => o.kundeId === kundeId);

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        kundeId,
        titel,
        gueltigBis: gueltigBis || undefined,
        zahlungszielTage: Number(zahlungszielTage),
        positionen: positionen.map((p) => {
          const rabatt = parseMoneyOr(p.rabattProzent, ZERO_MONEY);
          return {
            bezeichnung: p.bezeichnung,
            ...(p.beschreibung.trim() ? { beschreibung: p.beschreibung.trim() } : {}),
            menge: p.menge,
            einheit: p.einheit,
            einzelpreisNetto: p.einzelpreisNetto,
            ustSatz: p.ustSatz,
            ...(rabatt !== ZERO_MONEY ? { rabattProzent: rabatt } : {}),
          };
        }),
      };
      if (objektId) body['objektId'] = objektId;
      if (notizen.trim()) body['notizen'] = notizen.trim();
      return api<{ item: { id: string } }>('/api/v1/angebote', { method: 'POST', body });
    },
    onSuccess: async (res) => {
      await router.navigate({ to: '/angebote/$id', params: { id: res.item.id } });
    },
    onError: (err) => setError(err),
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    save.mutate();
  }

  const summen = computeSummen(positionen);

  return (
    <div className="space-y-4">
      <div>
        <Link to="/angebote" className="text-sm text-brand-700 hover:underline">
          ← zurück zur Angebotsliste
        </Link>
      </div>
      <PageHeader
        title="Neues Angebot"
        description="Nummer wird beim Versenden aus dem Nummernkreis ANG gezogen. Bis dahin bleibt es ein frei editierbarer Entwurf."
      />

      <form onSubmit={handleSubmit} className="space-y-4">
        <section className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 space-y-3">
          <h2 className="text-sm font-semibold text-slate-900">Kopfdaten</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div>
              <label className="text-xs font-medium text-slate-600">Kunde *</label>
              <Select
                required
                value={kundeId}
                onChange={(e) => {
                  setKundeId(e.target.value);
                  setObjektId('');
                }}
              >
                <option value="">– wählen –</option>
                {(kundenQ.data?.items ?? []).map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.kundennummer} — {k.firmenname}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Objekt (optional)</label>
              <Select
                value={objektId}
                onChange={(e) => setObjektId(e.target.value)}
                disabled={!kundeId}
              >
                <option value="">–</option>
                {objekteDesKunden.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.objektnummer ? `${o.objektnummer} — ` : ''}
                    {o.bezeichnung}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Titel *</label>
              <Input required value={titel} onChange={(e) => setTitel(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Gültig bis</label>
              <Input
                type="date"
                value={gueltigBis}
                onChange={(e) => setGueltigBis(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Zahlungsziel (Tage)</label>
              <Input
                type="number"
                min="0"
                max="365"
                value={zahlungszielTage}
                onChange={(e) => setZahlungszielTage(e.target.value)}
              />
            </div>
            <div className="md:col-span-3">
              <label className="text-xs font-medium text-slate-600">Notizen (intern)</label>
              <textarea
                value={notizen}
                onChange={(e) => setNotizen(e.target.value)}
                rows={2}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 space-y-3">
          <header className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900">Positionen</h2>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setPositionen([...positionen, emptyPosition()])}
            >
              + Position
            </Button>
          </header>

          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr className="border-b border-slate-100">
                <th className="py-1.5 text-left w-8">#</th>
                <th className="py-1.5 text-left">Bezeichnung</th>
                <th className="py-1.5 text-right w-24">Menge</th>
                <th className="py-1.5 text-left w-20">Einheit</th>
                <th className="py-1.5 text-right w-28">Einzelpreis</th>
                <th className="py-1.5 text-right w-20">USt %</th>
                <th className="py-1.5 text-right w-20">Rabatt %</th>
                <th className="py-1.5 text-right w-28">Netto</th>
                <th className="py-1.5 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {positionen.map((p, idx) => {
                const posSummen = computeSummen([p]);
                return (
                  <tr key={idx}>
                    <td className="py-1.5 text-xs text-slate-400">{idx + 1}</td>
                    <td className="py-1.5 pr-2">
                      <Input
                        required
                        value={p.bezeichnung}
                        onChange={(e) =>
                          setPositionen(
                            positionen.map((q, i) =>
                              i === idx ? { ...q, bezeichnung: e.target.value } : q,
                            ),
                          )
                        }
                      />
                    </td>
                    <td className="py-1.5 pr-1">
                      <Input
                        required
                        type="number"
                        step="0.001"
                        min="0"
                        className="text-right tabular-nums"
                        value={p.menge}
                        onChange={(e) =>
                          setPositionen(
                            positionen.map((q, i) =>
                              i === idx ? { ...q, menge: e.target.value } : q,
                            ),
                          )
                        }
                      />
                    </td>
                    <td className="py-1.5 pr-1">
                      <Input
                        required
                        value={p.einheit}
                        onChange={(e) =>
                          setPositionen(
                            positionen.map((q, i) =>
                              i === idx ? { ...q, einheit: e.target.value } : q,
                            ),
                          )
                        }
                      />
                    </td>
                    <td className="py-1.5 pr-1">
                      <Input
                        required
                        type="number"
                        step="0.01"
                        min="0"
                        className="text-right tabular-nums"
                        value={p.einzelpreisNetto}
                        onChange={(e) =>
                          setPositionen(
                            positionen.map((q, i) =>
                              i === idx ? { ...q, einzelpreisNetto: e.target.value } : q,
                            ),
                          )
                        }
                      />
                    </td>
                    <td className="py-1.5 pr-1">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        className="text-right tabular-nums"
                        value={p.ustSatz}
                        onChange={(e) =>
                          setPositionen(
                            positionen.map((q, i) =>
                              i === idx ? { ...q, ustSatz: e.target.value } : q,
                            ),
                          )
                        }
                      />
                    </td>
                    <td className="py-1.5 pr-1">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        className="text-right tabular-nums"
                        value={p.rabattProzent}
                        onChange={(e) =>
                          setPositionen(
                            positionen.map((q, i) =>
                              i === idx ? { ...q, rabattProzent: e.target.value } : q,
                            ),
                          )
                        }
                      />
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-slate-600">
                      {formatEuroDe(posSummen.netto)}
                    </td>
                    <td className="py-1.5 text-center">
                      <button
                        type="button"
                        onClick={() => setPositionen(positionen.filter((_, i) => i !== idx))}
                        disabled={positionen.length === 1}
                        className="text-slate-400 hover:text-red-600 disabled:text-slate-200 disabled:cursor-not-allowed"
                        title="Position entfernen"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-200 text-sm font-medium">
                <td colSpan={7} className="py-1.5 text-right text-slate-600">
                  Summe netto
                </td>
                <td className="py-1.5 text-right tabular-nums">{formatEuroDe(summen.netto)} €</td>
                <td></td>
              </tr>
              <tr className="text-sm text-slate-600">
                <td colSpan={7} className="py-1 text-right">
                  USt
                </td>
                <td className="py-1 text-right tabular-nums">{formatEuroDe(summen.ust)} €</td>
                <td></td>
              </tr>
              <tr className="text-sm font-semibold">
                <td colSpan={7} className="py-1 text-right">
                  Brutto
                </td>
                <td className="py-1 text-right tabular-nums">{formatEuroDe(summen.brutto)} €</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </section>

        <ErrorBanner error={error} />

        <div className="flex justify-end gap-2">
          <Link
            to="/angebote"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium bg-transparent hover:bg-slate-100 text-slate-700 transition-colors"
          >
            Abbrechen
          </Link>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Speichere…' : 'Als Entwurf speichern'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function defaultGueltigBis(): string {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}
